/**
 * profileCompleteness.js — iter1092 — Feature-to-data mapping + completeness scorer.
 *
 * iter1089 (стар) четеше FLAT keys (`p.age`, `p.height_cm`) — но реалният
 * profile shape е NESTED (`profile.personal.age`, `profile.body.height_cm`,
 * `profile.lifestyle.activityLevel` в camelCase). Резултат: banner-a
 * винаги показваше 0% дори за напълно попълнени профили.
 *
 * Тази версия чете от истинските nested paths и предоставя `deriveCyclePhase`
 * помощник за жени, които имат само LMP + средна дължина.
 */

// ─── Path helpers ───────────────────────────────────────────────────────
function readAny(obj, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    for (const seg of parts) {
      if (cur == null) { cur = null; break; }
      cur = cur[seg];
    }
    if (cur !== null && cur !== undefined && cur !== "") return cur;
  }
  return null;
}

const num = (v) => (typeof v === "number" ? v : Number(v));
const isPositive = (v) => {
  const n = num(v);
  return Number.isFinite(n) && n > 0;
};

// ─── Field mapping — реалният shape според `/api/auth/me` ────────────
export function readProfileFields(p = {}) {
  return {
    age: readAny(p, ["personal.age", "age"]),
    gender: readAny(p, ["personal.gender", "gender", "sex"]),
    height_cm: readAny(p, ["body.height_cm", "body.height", "personal.height_cm", "height_cm"]),
    weight_kg: readAny(p, ["body.weight_kg", "body.weight", "personal.weight_kg", "weight_kg"]),
    activity_level: readAny(p, ["lifestyle.activityLevel", "lifestyle.activity_level", "personal.activity_level", "activity_level"]),
    sleep_hours: readAny(p, ["lifestyle.sleepHours", "lifestyle.sleep_hours", "sleep_hours"]),
    stress_level: readAny(p, ["lifestyle.stressLevel", "lifestyle.stress_level", "stress_level"]),
    primary_goal: readAny(p, ["lifestyle.primaryGoal", "lifestyle.primary_goal", "personal.goal", "primary_goal"]),
    conditions: (() => {
      const c = readAny(p, ["conditions", "current_conditions", "health.currentConditions"]);
      if (Array.isArray(c)) return c;
      if (typeof c === "string" && c.trim()) return [c];
      return [];
    })(),
    lastPeriodDate: readAny(p, ["lastPeriodDate", "reproductive.lastPeriodDate", "reproductive.last_period_date"]),
    avgCycleLength: readAny(p, ["avgCycleLength", "reproductive.avgCycleLength", "reproductive.cycle_length"]),
    cycle_phase: readAny(p, ["cycle_phase", "reproductive.cycle_phase"]),
    perinatal_status: readAny(p, ["perinatal_status", "reproductive.perinatal_status", "reproductive.hrt_status"]),
    breastfeeding_months: readAny(p, ["reproductive.breastfeeding_months"]),
    parity: readAny(p, ["reproductive.parity"]),
  };
}

/**
 * Derive текуща фаза на цикъла от LMP (last menstrual period) + средна дължина.
 * Използва стандартна 4-phase моделa (menstrual / follicular / ovulatory / luteal).
 * Връща `null` ако липсват данни.
 */
export function deriveCyclePhase(lastPeriodDate, avgCycleLength) {
  if (!lastPeriodDate) return null;
  const length = num(avgCycleLength);
  const cycleLen = Number.isFinite(length) && length >= 20 && length <= 45 ? length : 28;
  const lmp = new Date(lastPeriodDate);
  if (isNaN(lmp.getTime())) return null;
  const dayOfCycle = Math.floor((Date.now() - lmp.getTime()) / (1000 * 60 * 60 * 24)) % cycleLen;
  const d = dayOfCycle < 0 ? dayOfCycle + cycleLen : dayOfCycle;
  if (d < 5) return "menstrual";
  if (d < Math.floor(cycleLen / 2) - 2) return "follicular";
  if (d < Math.floor(cycleLen / 2) + 2) return "ovulatory";
  return "luteal";
}

// ─── Секции ────────────────────────────────────────────────────────────
export const PROFILE_SECTIONS = [
  {
    key: "basics",
    label_bg: "Основен профил",
    label_en: "Basic profile",
    weight: 25,
    unlocks_bg: ["BMR/TDEE изчисление", "DIL бюджет", "Всички дневни оценки"],
    action_route: "/dashboard/profile?tab=profile&edit=basics",
  },
  {
    key: "lifestyle",
    label_bg: "Начин на живот",
    label_en: "Lifestyle",
    weight: 10,
    unlocks_bg: ["Персонализирани препоръки", "Stress twin"],
    action_route: "/dashboard/profile?tab=profile&edit=lifestyle",
  },
  {
    key: "conditions",
    label_bg: "Здравословни състояния",
    label_en: "Conditions",
    weight: 15,
    unlocks_bg: ["Nutrition Lab персонализация", "Контекстни оценки"],
    action_route: "/dashboard/profile?tab=profile&edit=health",
  },
  {
    key: "labs",
    label_bg: "Лабораторни изследвания",
    label_en: "Lab results",
    weight: 20,
    unlocks_bg: ["Lab twin", "Inflammation twin", "Metabolic twin", "Kidney/Liver twins"],
    action_route: "/dashboard/profile?tab=mydata",
  },
  {
    key: "cycle_perinatal",
    label_bg: "Цикъл / перинатал",
    label_en: "Cycle / perinatal",
    weight: 10,
    female_only: true,
    unlocks_bg: ["Hormonal twin", "Breast Intelligence"],
    action_route: "/dashboard/profile?tab=profile&edit=cycle",
  },
  {
    key: "twin_ready",
    label_bg: "Данни за близнаци",
    label_en: "Twin data",
    weight: 20,
    unlocks_bg: ["20-те дигитални близнаци", "Cross-twin insights"],
    action_route: "/dashboard/profile?tab=mydata",
  },
];

/**
 * Изчислява процент попълненост от профил обект + external counters.
 * @param {Object} p — може да е `profile` от `/api/auth/me` (nested shape)
 *   или merged obj с добавени counters (`lab_snapshots_count`, `twin_readiness_pct`).
 * @param {string} sex — "male" | "female". Ако липсва, взима се от profile.
 */
export function computeCompleteness(p = {}, sex = null) {
  const f = readProfileFields(p);
  const sx = String(sex || f.gender || "").toLowerCase();
  const isFemale = sx === "female" || sx === "f" || sx === "ж" || sx === "жена";

  // Twin readiness — приемаме или `twin_readiness_pct` (external merged),
  // или `coverage_pct` от twin-score endpoint.
  const twinReadiness =
    num(p.twin_readiness_pct)
    || num(p.coverage_pct)
    || 0;

  const labsCount =
    num(p.lab_snapshots_count)
    || (Array.isArray(p.labs) ? p.labs.length : 0)
    || (Array.isArray(p.labValues) ? p.labValues.length : 0);

  // Cycle секция: complete ако имаме явна фаза, или LMP + дължина (=> може да се derive-не), или perinatal статус.
  const cycleComplete = isFemale
    ? (!!f.cycle_phase
       || (!!f.lastPeriodDate && !!f.avgCycleLength)
       || (!!f.perinatal_status && f.perinatal_status !== "none")
       || (num(f.breastfeeding_months) > 0))
    : true;

  const filled = {
    basics: isPositive(f.age) && !!f.gender && isPositive(f.height_cm) && isPositive(f.weight_kg),
    lifestyle: !!f.activity_level && !!f.sleep_hours && !!f.primary_goal,
    conditions: f.conditions && f.conditions.length > 0,
    labs: labsCount > 0,
    cycle_perinatal: cycleComplete,
    twin_ready: twinReadiness >= 50,
  };

  let total = 0;
  let earned = 0;
  const sections = PROFILE_SECTIONS.map((s) => {
    if (s.female_only && !isFemale) return { ...s, applicable: false, complete: true };
    total += s.weight;
    const done = !!filled[s.key];
    if (done) earned += s.weight;
    return { ...s, applicable: true, complete: done };
  });

  const pct = total > 0 ? Math.round((earned / total) * 100) : 0;
  return { pct, sections, fields: f };
}
